import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { KeyRound, ShieldAlert } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { useChangeOwnPassword } from '@/features/admin/passwordApi'
import { useAuthStore } from '@/lib/auth/store'

/**
 * Forced password-change screen. The user lands here when login returns
 * {@code passwordResetRequired: true} — the backend gate confines them to
 * this endpoint until they self-change. Successful change revokes every
 * session, so we explicitly log out and bounce them to /login.
 */
export function ForceChangePasswordPage() {
  const navigate = useNavigate()
  const logout = useAuthStore((s) => s.logout)
  const change = useChangeOwnPassword()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  const newPasswordTooShort = newPassword.length > 0 && newPassword.length < 8
  const mismatch = confirm.length > 0 && confirm !== newPassword
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 8 && confirm === newPassword
  const isMatchingOld = newPassword.length > 0 && currentPassword === newPassword

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    change.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          // The backend revokes every session on a successful change —
          // even our current access token is now invalid. Log out
          // locally so the user gets a clean /login redirect with the
          // success cue.
          logout('user_initiated')
          navigate({ to: '/login' })
        },
      },
    )
  }

  const errorMessage = (() => {
    if (!change.isError) return null
    if (!isAxiosError(change.error)) return 'Could not change password. Try again.'
    const status = change.error.response?.status
    if (status === 400) {
      const detail = change.error.response?.data?.detail
      if (detail?.includes('current password')) return 'Your current password is incorrect.'
      if (detail?.includes('at least 8')) return 'New password must be at least 8 characters.'
      return detail ?? 'Validation failed.'
    }
    return 'Could not change password. Try again.'
  })()

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-warning" />
            <CardTitle>Set a new password</CardTitle>
          </div>
          <CardDescription>
            Your admin reset your password — pick a new one to continue.
            You'll be signed out of all your sessions on the way through.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label>Temporary password</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <p className="mt-1 text-2xs uppercase tracking-wider text-muted-foreground">
                The one your admin shared with you.
              </p>
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                required
              />
              {newPasswordTooShort && (
                <p className="mt-1 text-2xs text-negative">At least 8 characters.</p>
              )}
              {isMatchingOld && (
                <p className="mt-1 text-2xs text-negative">Pick a different password than the temporary one.</p>
              )}
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
              {mismatch && (
                <p className="mt-1 text-2xs text-negative">Doesn't match the new password.</p>
              )}
            </div>
            {errorMessage && (
              <Alert variant="destructive">
                <AlertTitle>Couldn't change password</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" disabled={!canSubmit || isMatchingOld || change.isPending} className="w-full">
              <KeyRound className="h-3.5 w-3.5" />
              {change.isPending ? 'Updating…' : 'Set new password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
